import type { LC, LiveElement, ArrowFunction } from '@use-gpu/live';
import type { UseGPURenderContext, Lazy } from '@use-gpu/core';
import { gather, provide, unquote, use, memo, yeet, useHooks, useMemo, useOne, useRef, SUSPEND } from '@use-gpu/live';
import { useRenderProp } from '../hooks/useRenderProp';
import { usePerViewFrame, useNoPerViewFrame } from '../providers/view-frame-provider';
import { useAnimationFrame, useNoAnimationFrame } from '../providers/loop-provider';
import { SuspenseContext } from '../providers/suspense-provider';
import { QueueReconciler } from '../reconcilers/index';
import { RenderToTexture } from '../render/render-to-texture';

const {reconcile, quote, signal} = QueueReconciler;

type RenderProp = (frame: Lazy<number>) => LiveElement;
type ThenProp = (frame: Lazy<number>, converged: Lazy<boolean>) => LiveElement;

export type AccumulateRenderProps = {
  target: UseGPURenderContext,

  continued?: boolean,
  /** Number of frames before converged = true (default = 1) */
  frames?: number,
  /** Number of frames to render before stopping */
  limit?: number,
  version?: number,

  render?: RenderProp,
  children?: RenderProp,

  then?: ThenProp,
};

/** Iteration combinator for gathered render passes */
export const AccumulateRender: LC<AccumulateRenderProps> = memo((props: AccumulateRenderProps) => {
  const {
    target,
    continued = false,
    frames = 1,
    version,
    limit,

    then,
  } = props;

  const frameRef = useRef(0);
  const convergedRef = useRef(false);

  // Reset frame count if view or version changed
  useOne(() => {
    frameRef.current = 0;
  }, version === undefined ? usePerViewFrame() : (useNoPerViewFrame(), version));

  // Request new animation frame if frames remain
  const {current: frame} = frameRef;
  const shouldRequest = !(limit != null && frame >= limit);
  shouldRequest ? useAnimationFrame() : useNoAnimationFrame();

  const children = useRenderProp(props, frameRef);
  const next = useHooks(() => then?.(frameRef, convergedRef), [then]);

  return useMemo(() => [
    reconcile(
      quote(
        gather(
          unquote(use(RenderToTexture, {target, children:
            provide(SuspenseContext, true, children),
          })),
          (fsOrSuspend: ArrowFunction[] | typeof SUSPEND) => {
            if (fsOrSuspend === SUSPEND) return;
            const fs = fsOrSuspend as ArrowFunction[];

            useMemo(() => {
              if (!continued) frameRef.current = 0;
              // eslint-disable-next-line react-hooks/exhaustive-deps
            }, [continued, ...fs]);

            return [
              signal(),
              useMemo(() => {
                const run = () => {
                  const {current: frame} = frameRef;
                  if (limit != null && frame >= limit) return;

                  for (const f of fs) f();
                  frameRef.current++;
                  convergedRef.current = frameRef.current >= frames;
                };

                return quote(yeet(run));
                // eslint-disable-next-line react-hooks/exhaustive-deps
              }, fs),
            ];
          },
          SUSPEND,
        )
      )
    ),
    next,
  ], [target, children, next, continued, limit, frames]);
}, 'AccumulateRender');
